import { filter, map, includes, toLower } from "lodash";
import React, { useState, useEffect } from "react";
import Button from "antd/lib/button";
import Dropdown from "antd/lib/dropdown";
import Menu from "antd/lib/menu";
import DownOutlinedIcon from "@ant-design/icons/DownOutlined";
import { useTheme } from "@/components/ThemeProvider";

import routeWithUserSession from "@/components/application-area/routeWithUserSession";
import navigateTo from "@/components/application-area/navigateTo";
import Paginator from "@/components/items-list/components/Paginator";

import { wrap as itemsList } from "@/components/items-list/ItemsList";
import { ResourceItemsSource } from "@/components/items-list/classes/ItemsSource";
import { StateStorage } from "@/components/items-list/classes/StateStorage";

import LoadingState from "@/components/items-list/components/LoadingState";
import ItemsTable, { Columns } from "@/components/items-list/components/ItemsTable";
import SelectItemsDialog from "@/components/select-items-dialog/SelectItemsDialog";
import { DataSourcePreviewCard } from "@/components/PreviewCard";

import GroupName from "@/components/groups/GroupName";
import ListItemAddon from "@/components/groups/ListItemAddon";
import Sidebar from "@/components/groups/DetailsPageSidebar";
import Layout from "@/components/layouts/ContentWithSidebar";
import wrapSettingsTab from "@/components/settings/SettingsWrapper";

import notification from "@/services/notification";
import { currentUser } from "@/services/auth";
import Group from "@/services/group";
import DataSource from "@/services/data-source";
import routes from "@/services/routes";

import "./Groups.less";

function GroupDataSources({ controller }) {
  const { isDarkMode } = useTheme();
  const [group, setGroup] = useState(null);
  const groupId = parseInt(controller.params.groupId, 10);

  useEffect(() => {
    Group.get({ id: groupId })
      .then(fetchedGroup => {
        setGroup(fetchedGroup);
      })
      .catch(error => {
        controller.handleError(error);
      });
  }, [groupId, controller]);

  const removeGroupDataSource = datasource => {
    Group.removeDataSource({ id: groupId, dataSourceId: datasource.id })
      .then(() => {
        controller.updatePagination({ page: 1 });
        controller.update();
      })
      .catch(() => {
        notification.error("Failed to remove data source from group.");
      });
  };

  const setDataSourcePermissions = (datasource, permission) => {
    const viewOnly = permission !== "full";

    Group.updateDataSource({ id: groupId, dataSourceId: datasource.id }, { view_only: viewOnly })
      .then(() => {
        datasource.view_only = viewOnly;
        controller.update();
      })
      .catch(() => {
        notification.error("Failed change data source permissions.");
      });
  };

  const addDataSources = () => {
    const allDataSources = DataSource.query();
    const alreadyAddedDataSources = map(controller.allItems, ds => ds.id);
    SelectItemsDialog.showModal({
      dialogTitle: "Add Data Sources",
      inputPlaceholder: "Search data sources...",
      selectedItemsTitle: "New Data Sources",
      searchItems: searchTerm => {
        searchTerm = toLower(searchTerm);
        return allDataSources.then(items => filter(items, ds => includes(toLower(ds.name), searchTerm)));
      },
      renderItem: (item, { isSelected }) => {
        const alreadyInGroup = includes(alreadyAddedDataSources, item.id);
        return {
          content: (
            <DataSourcePreviewCard dataSource={item}>
              <ListItemAddon isSelected={isSelected} alreadyInGroup={alreadyInGroup} />
            </DataSourcePreviewCard>
          ),
          isDisabled: alreadyInGroup,
          className: isSelected || alreadyInGroup ? "selected" : "",
        };
      },
      renderStagedItem: (item, { isSelected }) => ({
        content: (
          <DataSourcePreviewCard dataSource={item}>
            <ListItemAddon isSelected={isSelected} isStaged />
          </DataSourcePreviewCard>
        ),
      }),
    }).onClose(items => {
      const promises = map(items, ds => Group.addDataSource({ id: groupId }, { data_source_id: ds.id }));
      return Promise.all(promises).then(() => controller.update());
    });
  };

  const sidebarMenu = [
    {
      key: "users",
      href: `groups/${groupId}`,
      title: "Members",
    },
    {
      key: "datasources",
      href: `groups/${groupId}/data_sources`,
      title: "Data Sources",
      isAvailable: () => currentUser.isAdmin,
    },
  ];

  const listColumns = [
    Columns.custom((text, datasource) => <DataSourcePreviewCard dataSource={datasource} withLink />, {
      title: "Name",
      field: "name",
      width: null,
    }),
    Columns.custom(
      (text, datasource) => {
        const menu = (
          <Menu
            selectedKeys={[datasource.view_only ? "viewonly" : "full"]}
            onClick={item => setDataSourcePermissions(datasource, item.key)}>
            <Menu.Item key="full">Full Access</Menu.Item>
            <Menu.Item key="viewonly">View Only</Menu.Item>
          </Menu>
        );

        return (
          <Dropdown trigger={["click"]} overlay={menu}>
            <Button className="w-100" aria-label="Permissions">
              {datasource.view_only ? "View Only" : "Full Access"}
              <DownOutlinedIcon aria-hidden="true" />
            </Button>
          </Dropdown>
        );
      },
      {
        width: "1%",
        className: "p-r-0",
        isAvailable: () => currentUser.isAdmin,
      }
    ),
    Columns.custom(
      (text, datasource) => (
        <Button className="w-100" type="danger" onClick={() => removeGroupDataSource(datasource)}>
          Remove
        </Button>
      ),
      {
        width: "1%",
        isAvailable: () => currentUser.isAdmin,
      }
    ),
  ];

  return (
    <div className={`groups-page ${isDarkMode ? 'dark-mode' : ''}`} data-test="Group">
      <GroupName className="d-block m-t-0 m-b-15" group={group} onChange={() => setGroup({ ...group })} />
      <Layout>
        <Layout.Sidebar>
          <Sidebar
            controller={controller}
            group={group}
            items={sidebarMenu}
            canAddDataSources={currentUser.isAdmin}
            onAddDataSourcesClick={addDataSources}
            onGroupDeleted={() => navigateTo("groups")}
          />
        </Layout.Sidebar>
        <Layout.Content>
          {!controller.isLoaded && <LoadingState className="" />}
          {controller.isLoaded && controller.isEmpty && (
            <div className="text-center">
              <p>There are no data sources in this group yet.</p>
              {currentUser.isAdmin && (
                <Button type="primary" onClick={addDataSources}>
                  <i className="fa fa-plus m-r-5" aria-hidden="true" />
                  Add Data Sources
                </Button>
              )}
            </div>
          )}
          {controller.isLoaded && !controller.isEmpty && (
            <div className="table-responsive">
              <ItemsTable
                items={controller.pageItems}
                columns={listColumns}
                showHeader={false}
                // Remove the context prop or replace it if needed
                orderByField={controller.orderByField}
                orderByReverse={controller.orderByReverse}
                toggleSorting={controller.toggleSorting}
              />
              <Paginator
                showPageSizeSelect
                totalCount={controller.totalItemsCount}
                pageSize={controller.itemsPerPage}
                onPageSizeChange={itemsPerPage => controller.updatePagination({ itemsPerPage })}
                page={controller.page}
                onChange={page => controller.updatePagination({ page })}
              />
            </div>
          )}
        </Layout.Content>
      </Layout>
    </div>
  );
}

const GroupDataSourcesPage = wrapSettingsTab(
  "Groups.DataSources",
  null,
  itemsList(
    GroupDataSources,
    () =>
      new ResourceItemsSource({
        isPlainList: true,
        getRequest(unused, { params: { groupId } }) {
          return { id: groupId };
        },
        getResource() {
          return Group.dataSources.bind(Group);
        },
      }),
    () => new StateStorage({ orderByField: "name" })
  )
);

routes.register(
  "Groups.DataSources",
  routeWithUserSession({
    path: "/groups/:groupId/data_sources",
    title: "Group Data Sources",
    render: pageProps => <GroupDataSourcesPage {...pageProps} currentPage="datasources" />,
  })
);
